import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Button, Grid, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { Suspense, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { get, getOrgAlias } from "../../utils/io";
import MainContentContainer from "../Layouts/MainContentContainer";
import AlertRules from "./AlertRules";
import {
  ALERT_RULES_CHF,
  ALERT_RULES_CKD,
  ALERT_RULES_COPD,
  ALERT_RULES_HTN,
} from "./customPrograms/guthrie/constants";
import convertConfig from "./customPrograms/utils/configConverter";

export default function DynamicProgramDashboard() {
  const { programId } = useParams();
  const orgAlias = getOrgAlias();

  const [program, setProgram] = useState({});
  const [ProgramComponent, setProgramComponent] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [alertRules, setAlertRules] = useState();

  const handleAlertRules = () => {
    setIsOpen(!isOpen);
  };

  // Required to ensure webpack packages the necessary files
  const customProgramsModuleContext = require.context(
    "./customPrograms",
    true,
    /\.(js|jsx)$/
  );

  useEffect(() => {
    (async () => {
      const res = await get(`/programs/${programId}`);
      if (res?.status !== 200) {
        enqueueSnackbar("Unable to retrieve program details", {
          variant: "error",
        });
        return;
      }

      const { data } = res;
      setProgram(data);

      if (data.is_dynamic && data.config) {
        const componentModule = React.lazy(() =>
          import("./customPrograms/DynamicProgram")
        );
        setProgramComponent(() => componentModule);
      } else {
        const dynamicDashboardComponent = data.ui_component;
        /*
        Program components are always in the `customProgams` dir and assumed
        to be contained in a subdireectory named with the org's alias. However,
        if necessary a component can be shared across orgs and the path to the
        component can be prefixed with '@' to signify the path is relative to the
        customPrograms root.

        Since a require.context is used, paths must include exact file names,
        included extensions (.js) and if using an index file, /index.js
        */
        const relativeComponentPath = `${
          dynamicDashboardComponent.startsWith("@")
            ? dynamicDashboardComponent.substring(1)
            : `${orgAlias}/${dynamicDashboardComponent}`
        }`;
        const componentModule = React.lazy(() =>
          Promise.resolve(
            customProgramsModuleContext(`./${relativeComponentPath}`)
          )
        );

        setProgramComponent(() => componentModule);
      }
    })();
  }, [programId, orgAlias, customProgramsModuleContext]);

  useEffect(() => {
    if (program.is_dynamic && program.config) {
      const convertedRules = convertConfig(program.config);
      setAlertRules(convertedRules);
    } else {
      const rulesMap = {
        CHF: ALERT_RULES_CHF,
        COPD: ALERT_RULES_COPD,
        CKD: ALERT_RULES_CKD,
        HTN: ALERT_RULES_HTN,
      };
      setAlertRules(rulesMap[program?.ui_component?.split("/")[1]] || null);
    }
  }, [program]);
  return (
    <MainContentContainer
      title={
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Typography
            component="h1"
            sx={{ fontSize: 24, fontWeight: 700, margin: 0 }}
          >
            {program?.name || "Loading..."}
          </Typography>
          {alertRules && (
            <Button
              sx={{ display: "flex" }}
              color="primary"
              type="submit"
              variant="outlined"
              startIcon={<ErrorOutlineIcon />}
              onClick={handleAlertRules}
            >
              ALERT RULES
            </Button>
          )}
        </Grid>
      }
    >
      <Suspense fallback="Loading...">
        {!ProgramComponent ? (
          "Loading..."
        ) : (
          <ProgramComponent programId={program.id} program={program} />
        )}
      </Suspense>
      {alertRules && (
        <AlertRules
          alertRules={alertRules}
          isOpen={isOpen}
          handleAlertRules={handleAlertRules}
        />
      )}
    </MainContentContainer>
  );
}
